// @ts-nocheck
import { XGInput, XGRadio } from 'xg-cl';
import { formatCurrency, getConfig } from '../../../utils/currencyHelper';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ErrorToast } from '../../../utils/alerts';
import { updatePaymentIntent } from '../../../services/stripe/stripe';
import { createTransaction } from '../../../services/transaction/transaction';
import { createBookingPassengerAddOnsBulk } from '../../../services/booking/booking';
import { initGA, trackEvent } from '../../../utils/analytics';
import {
  paymentElementOptions,
  requiresFullBillingAddress,
  successfulStatuses
} from '../../../constants/stripe';
import { useNavigate } from 'react-router';
import {
  useStripe,
  useElements,
  PaymentElement,
  AddressElement
} from '@stripe/react-stripe-js';
import { format } from 'date-fns';
import { toUTCNoonDate } from '../../../utils/dateUtility';
import { getFeesBreakdown } from '../../../utils/feesHelper';
import { useLocation } from 'react-router-dom';
import PaymentStatusIcon from '../../Common/PaymentStatusIcon';
import * as PropTypes from 'prop-types';
import './PaymentForm.css';
import { getSecondaryFullPaymentAmount } from '../../../utils/secondaryPayment';
import { sanitizeStripeReturnUrl } from '../../../utils/stripeReturnUrl';
import {
  buildFxTransactionFields,
  hasFxPaymentSelection
} from '../../../utils/fxTransactionFields';

/**
 * PaymentForm is a React component that displays a payment form to the user.
 * It allows the user to select a payment option and enter their payment details.
 * The component also handles the submission of the payment form and
 * updates the payment intent accordingly. After successful payment, it saves
 * any newly selected add-ons to the database.
 * @param {{ id: string, data: object, paymentIntentId: string, setPaymentMethod: function, paymentMethodOrder: string[], paymentOption: string, setPaymentOption: function, partialAmount: string, setPartialAmount: function, nextInstallment: object, pastDueAmount: number, allSelectedAddOnsTotal: number, settings: object, submitRef: React.MutableRefObject<HTMLInputElement>, customForm: object, setCustomForm: function, customFormError: object, setCustomFormError: function, partialAmountError: string, setPartialAmountError: function, setIsProcessing: function, paymentSchedule: object[], newAddOnsPayload: array, addOnsSection: node }}
 * @returns {React.ReactElement}
 */
const PaymentForm = ({
  id,
  data,
  paymentIntentId,
  selectedCurrency,
  currencyState,
  currencyConfig,
  onPaymentConfirmed,
  setPaymentMethod,
  paymentMethodOrder,
  paymentOption,
  setPaymentOption,
  partialAmount,
  setPartialAmount,
  nextInstallment,
  pastDueAmount,
  allSelectedAddOnsTotal = 0,
  deferredAddOnsTotal = 0,
  showAddToBalanceOption = false,
  settings,
  submitRef,
  customForm,
  setCustomForm,
  customFormError,
  setCustomFormError,
  partialAmountError,
  setPartialAmountError,
  setIsProcessing,
  paymentSchedule = [],
  newAddOnsPayload = [],
  addOnsSection = null,
  bookingPaidInFull = false,
  onManualPaymentSelection,
  ...rest
}) => {
  const { currencyCode, currencySymbol } = getConfig(currencyConfig);
  const operatorCurrency =
    data?.tenant?.sellingcurrency?.currencycode ||
    data?.tenant?.basecurrency?.currencycode ||
    currencyCode;
  const hasFxSelection = hasFxPaymentSelection(
    selectedCurrency,
    operatorCurrency
  );
  const { t } = useTranslation(['trip', 'bookings', 'common', 'errors']);
  const { bookingid, balancedue, fees } = data;
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const primaryColor = settings?.color || 'var(--primary-600, #1361d8)';
  const dateFormat = t('common:dateFormat');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const bookingKey = queryParams.get('bookingkey');
  const [previousPaymentMethod, setPreviousPaymentMethod] = useState(null);
  const submitting = useRef(false);
  const confirmedPayment = useRef(null);
  const managed = Boolean(currencyState?.managed);
  const operatorMoney = (amount) => formatCurrency(amount, currencyConfig);
  const completionLocked =
    Boolean(confirmedPayment.current) || currencyState?.completed;

  const allowManualPartial =
    data.trip?.paymentplan?.allowpartialpayment ?? false;
  const hasInstallmentOption =
    Boolean(nextInstallment) && !isNaN(Number(nextInstallment?.amount));
  const nextInstallmentAmount = hasInstallmentOption
    ? Number(nextInstallment?.amount) || 0
    : 0;

  const pastDueTotal = Number(pastDueAmount) || 0;
  const remainingBalance = Number(balancedue) || 0;
  const hasPastDueOption = pastDueTotal > 0;
  const additionalAddonsAmount = bookingPaidInFull ? allSelectedAddOnsTotal : 0;
  const isAddToBalanceSelected = paymentOption === 'addToBalance';
  const displayPaymentSection = isAddToBalanceSelected
    ? true
    : !bookingPaidInFull || additionalAddonsAmount > 0;

  // Check if next installment is final payment
  const isNextInstallmentFinal = useMemo(() => {
    if (!hasInstallmentOption) return false;
    // If next installment amount equals remaining balance, it's the final payment
    return nextInstallmentAmount >= remainingBalance;
  }, [hasInstallmentOption, nextInstallmentAmount, remainingBalance]);

  // Generate payment schedule breakdown for "Pay Next Installment" option
  const getPaymentBreakdown = () => {
    if (!paymentSchedule || paymentSchedule.length === 0) return [];

    const today = new Date();
    const todayString = format(today, 'yyyy-MM-dd');

    return paymentSchedule.map((payment) => {
      const formattedDate = format(toUTCNoonDate(payment.date), dateFormat);
      // Extract date portion directly to avoid timezone conversion issues
      const paymentDateString = payment.date?.split('T')?.[0] || payment.date;

      // Determine payment status
      let status = 'upcoming';
      let description = '';

      // Check isPaid flag first (if payment has been received)
      if (payment.isPaid) {
        status = 'completed';
        description =
          payment.type === 'booking'
            ? 'Paid at booking'
            : `Paid ${formattedDate}`;
      } else if (payment.type === 'final' || payment.isFinalPayment) {
        status = 'upcoming';
        description = `Final Payment ${formattedDate}`;
      } else {
        // Check if payment is overdue, due today, or upcoming
        if (paymentDateString < todayString) {
          status = 'overdue';
        } else if (paymentDateString === todayString) {
          status = 'due-today';
        } else {
          status = 'upcoming';
        }
        description =
          payment.type === 'booking'
            ? 'Due at booking'
            : `Due ${formattedDate}`;
      }

      // If payment is fully paid, show original amount; otherwise show outstanding amount
      const displayAmount = payment.isPaid
        ? payment.amount
        : (payment.outstandingAmount ?? payment.amount);

      return {
        amount: displayAmount,
        description,
        date: payment.date,
        status,
        formattedAmount: operatorMoney(displayAmount)
      };
    });
  };

  const paymentBreakdown = getPaymentBreakdown();

  // CRITICAL: Payment choices define amounts DISPLAYED to user
  // These amounts MUST match the selectedAmount calculation below
  // See AGENTS.md - "Payment Processing Rules" section
  const paymentChoices = useMemo(() => {
    const choices = [];
    const hasNextInstallmentOption =
      hasInstallmentOption && nextInstallmentAmount > 0;

    //iF the booking is paid in full allow ONLY the "Pay Final Balance" option
    if (bookingPaidInFull) {
      choices.push({
        id: 'full',
        label: t('bookings:payFinalBalance'),
        amount: getSecondaryFullPaymentAmount({
          balanceDue: remainingBalance,
          selectedAddOnsTotal: allSelectedAddOnsTotal,
          bookingPaidInFull
        }),
        type: 'fixed'
      });

      if (showAddToBalanceOption) {
        choices.push({
          id: 'addToBalance',
          label: t('bookings:addToBalancePayLater'),
          amount: deferredAddOnsTotal,
          type: 'fixed',
          amountPrefix: '+'
        });
      }
    } else {
      // 1. Past due amount (always show if there is past due)
      // Note: pastDueTotal already includes add-ons from the payment schedule
      if (hasPastDueOption) {
        choices.push({
          id: 'pastDue',
          label: t('bookings:payPastDueAmount'),
          amount: pastDueTotal,
          type: 'fixed',
          showPaymentSchedule: true,
          paymentSchedule: paymentBreakdown
        });
      }

      // 2. Past due + next installment (if there is past due AND a future next installment that is not final)
      // Note: Both amounts already include add-ons from the payment schedule
      if (
        hasPastDueOption &&
        hasNextInstallmentOption &&
        !isNextInstallmentFinal
      ) {
        choices.push({
          id: 'pastDueAndNext',
          label: t('bookings:payPastDueAndNextInstallment'),
          amount: pastDueTotal + nextInstallmentAmount,
          type: 'fixed',
          showPaymentSchedule: true,
          paymentSchedule: paymentBreakdown
        });
      }

      // 3. Next installment (if there is a next installment that is not final/remaining balance and there are no past due payments)
      // Note: nextInstallmentAmount already includes add-ons from the payment schedule
      if (
        !hasPastDueOption &&
        hasNextInstallmentOption &&
        !isNextInstallmentFinal
      ) {
        choices.push({
          id: 'installment',
          label: t('bookings:payNextInstallment'),
          amount: nextInstallmentAmount,
          type: 'fixed',
          showPaymentSchedule: true,
          paymentSchedule: paymentBreakdown
        });
      }

      // 4. Partial amount (if enabled)
      if (allowManualPartial) {
        choices.push({
          id: 'partialManual',
          label: t('bookings:payPartialAmount'),
          type: 'input'
        });
      }

      // 5. Full amount (total remaining balance)
      if (remainingBalance > 0) {
        choices.push({
          id: 'full',
          label: t('bookings:payFinalBalance'),
          // Full balance payment must include all newly selected add-ons.
          amount: getSecondaryFullPaymentAmount({
            balanceDue: remainingBalance,
            selectedAddOnsTotal: allSelectedAddOnsTotal,
            bookingPaidInFull
          }),
          type: 'fixed'
        });
      }

      if (showAddToBalanceOption) {
        choices.push({
          id: 'addToBalance',
          label: t('bookings:addToBalancePayLater'),
          amount: deferredAddOnsTotal,
          type: 'fixed',
          amountPrefix: '+'
        });
      }
    }

    return choices;
  }, [
    allowManualPartial,
    hasInstallmentOption,
    nextInstallmentAmount,
    remainingBalance,
    hasPastDueOption,
    pastDueTotal,
    isNextInstallmentFinal,
    paymentBreakdown,
    allSelectedAddOnsTotal,
    deferredAddOnsTotal,
    showAddToBalanceOption,
    t,
    bookingPaidInFull
  ]);

  const hasMultipleOptions = paymentChoices.length > 1;

  // Auto-select default option
  useEffect(() => {
    if (bookingPaidInFull) {
      if (paymentOption !== 'full' && paymentOption !== 'addToBalance') {
        setPaymentOption('full');
      }
      return;
    }

    if (
      paymentOption &&
      paymentChoices.find((choice) => choice.id === paymentOption)
    ) {
      return;
    }

    const today = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(today.getDate() + 7);

    const nextInstallmentDate = nextInstallment?.date
      ? new Date(nextInstallment.date)
      : null;
    const hasInstallmentDueSoon =
      nextInstallmentDate &&
      nextInstallmentDate >= today &&
      nextInstallmentDate <= sevenDaysFromNow;

    if (
      hasPastDueOption &&
      paymentChoices.find((choice) => choice.id === 'pastDue')
    ) {
      setPaymentOption('pastDue');
      return;
    }

    if (
      hasInstallmentDueSoon &&
      paymentChoices.find((choice) => choice.id === 'installment')
    ) {
      setPaymentOption('installment');
      return;
    }

    if (paymentChoices.find((choice) => choice.id === 'full')) {
      setPaymentOption('full');
      return;
    }

    if (paymentChoices.length > 0) {
      setPaymentOption(paymentChoices[0].id);
    }
  }, [
    bookingPaidInFull,
    hasPastDueOption,
    nextInstallment?.date,
    paymentChoices,
    paymentOption,
    setPaymentOption
  ]);

  // CRITICAL: selectedAmount is the amount CHARGED to Stripe
  // This MUST match the display amounts in paymentChoices above
  // Each case must use identical calculation logic to its corresponding choice
  // See AGENTS.md - "Payment Processing Rules" section
  const selectedAmount = useMemo(() => {
    if (paymentOption === 'pastDue') {
      return pastDueTotal; // Must match choice.amount for 'pastDue'
    }
    if (paymentOption === 'pastDueAndNext') {
      return pastDueTotal + nextInstallmentAmount; // Must match choice.amount for 'pastDueAndNext'
    }
    if (paymentOption === 'installment') {
      return nextInstallmentAmount; // Must match choice.amount for 'installment'
    }
    if (paymentOption === 'partialManual') {
      const parsed = parseFloat(partialAmount);
      return isNaN(parsed) ? 0 : parsed; // User input, no display amount to match
    }
    if (paymentOption === 'addToBalance') {
      return 0;
    }
    // 'full' option - Must match choice.amount for 'full'
    return getSecondaryFullPaymentAmount({
      balanceDue: remainingBalance,
      selectedAddOnsTotal: allSelectedAddOnsTotal,
      bookingPaidInFull
    });
  }, [
    paymentOption,
    nextInstallmentAmount,
    partialAmount,
    remainingBalance,
    pastDueTotal,
    allSelectedAddOnsTotal,
    bookingPaidInFull
  ]);

  const handlePaymentOptionSelection = useCallback(
    (nextOption, isManual = false) => {
      setPaymentOption(nextOption);
      if (isManual) {
        onManualPaymentSelection?.();
      }
    },
    [onManualPaymentSelection, setPaymentOption]
  );

  // Development-time validation: Ensure displayed amount matches charge amount
  // NOTE: Must be AFTER selectedAmount declaration to avoid hoisting errors
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && paymentOption) {
      const selectedChoice = paymentChoices.find((c) => c.id === paymentOption);
      if (selectedChoice && selectedChoice.type !== 'input') {
        if (selectedChoice.id === 'addToBalance') {
          return;
        }

        const displayAmount = selectedChoice.amount;
        const chargeAmount = selectedAmount;

        if (Math.abs(displayAmount - chargeAmount) > 0.01) {
          console.error(
            '⚠️ PAYMENT AMOUNT MISMATCH DETECTED ⚠️\n' +
              `Payment option: ${selectedChoice.label}\n` +
              `Displayed amount: $${displayAmount.toFixed(2)}\n` +
              `Charge amount: $${chargeAmount.toFixed(2)}\n` +
              `Difference: $${Math.abs(displayAmount - chargeAmount).toFixed(2)}\n` +
              'The amount shown to the user MUST match the amount charged to Stripe!\n' +
              'Check PaymentForm.jsx - both paymentChoices and selectedAmount calculations.'
          );
        }
      }
    }
  }, [paymentOption, paymentChoices, selectedAmount]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (
      !confirmedPayment.current &&
      currencyState &&
      !currencyState.beginPayment(selectedCurrency)
    )
      return;
    submitting.current = true;
    try {
      const type =
        confirmedPayment.current?.type ||
        previousPaymentMethod?.type ||
        paymentMethodOrder?.[0] ||
        'card';

      if (!stripe || !elements) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      setIsProcessing(true);

      const fxTransactionFields =
        confirmedPayment.current?.fxTransactionFields ||
        buildFxTransactionFields(
          selectedCurrency,
          operatorCurrency,
          selectedAmount
        );

      const pendingIntent = confirmedPayment.current?.paymentIntent;
      const confirmation =
        (pendingIntent?.status === 'processing' && (hasFxSelection || managed)
          ? await stripe.retrievePaymentIntent(
              pendingIntent.client_secret ||
                currencyState?.intent?.clientSecret ||
                selectedCurrency?.clientSecret
            )
          : confirmedPayment.current) ||
        (await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: sanitizeStripeReturnUrl(window.location.href),
            payment_method_data: {
              billing_details: {
                email: customForm?.email.trim(),
                name: `${customForm?.firstName.trim()} ${customForm?.lastName.trim()}`
              }
            }
          },
          redirect: 'if_required' //This instruction avoid Stripe to redirect when getting the transaction result
        }));

      let error = confirmation.error;
      let paymentIntent =
        confirmation.paymentIntent || error?.payment_intent || {};
      if (
        (hasFxSelection || managed) &&
        paymentIntent.status === 'requires_action' &&
        stripe.handleNextAction
      ) {
        const action = await stripe.handleNextAction({
          clientSecret:
            currencyState?.intent?.clientSecret || paymentIntent.client_secret
        });
        if (action.error) error = action.error;
        const result = await stripe.retrievePaymentIntent(
          currencyState?.intent?.clientSecret || paymentIntent.client_secret
        );
        paymentIntent =
          result.paymentIntent || result.error?.payment_intent || paymentIntent;
      }
      if (
        (hasFxSelection || managed) &&
        paymentIntent.status === 'processing'
      ) {
        for (
          let attempt = 0;
          attempt < 3 && paymentIntent.status === 'processing';
          attempt++
        ) {
          if (attempt)
            await new Promise((resolve) => setTimeout(resolve, 1000));
          const result = await stripe.retrievePaymentIntent(
            currencyState?.intent?.clientSecret || paymentIntent.client_secret
          );
          paymentIntent =
            result.paymentIntent ||
            result.error?.payment_intent ||
            paymentIntent;
        }
      }
      const { status } = paymentIntent;

      const completedStatus =
        hasFxSelection || managed
          ? ['succeeded', 'processing'].includes(status)
          : successfulStatuses.includes(status);
      if (paymentIntent && completedStatus) {
        confirmedPayment.current ||= {
          paymentIntent,
          type,
          fxTransactionFields,
          operatorAmount: selectedAmount,
          newAddOnsPayload,
          customForm
        };
        currencyState?.endPayment(
          status === 'processing' && (hasFxSelection || managed)
            ? { unresolved: true }
            : { completed: true }
        );
        onPaymentConfirmed?.(true);
        confirmedPayment.current.paymentIntent = paymentIntent;
        if ((hasFxSelection || managed) && status !== 'succeeded') {
          ErrorToast(t('bookings:currencyPayment.paymentPending'));
          setIsProcessing(false);
          return;
        }
        const paymentSnapshot = confirmedPayment.current;
        // Save newly selected add-ons BEFORE creating transaction
        // This ensures they're in the DB when the payment confirmation email is sent
        if (
          !paymentSnapshot.addOnsSaveAttempted &&
          paymentSnapshot.newAddOnsPayload &&
          paymentSnapshot.newAddOnsPayload.length > 0
        ) {
          paymentSnapshot.addOnsSaveAttempted = true;
          try {
            await createBookingPassengerAddOnsBulk(bookingid, {
              addons: paymentSnapshot.newAddOnsPayload,
              shouldSendEmail: false
            });
          } catch (addOnError) {
            console.error('Error saving add-ons:', addOnError);
            // Don't block payment if add-ons fail to save
          }
        }

        const transactionPayload = {
          bookingid,
          amount:
            fxTransactionFields.amountappliedtobalance ??
            confirmedPayment.current.operatorAmount,
          stripetransactionid: paymentIntent?.id,
          paymentMethod: type,
          status,
          payersEmail: paymentSnapshot.customForm?.email.trim(),
          payersName: `${paymentSnapshot.customForm?.firstName.trim()} ${paymentSnapshot.customForm?.lastName.trim()}`,
          newAddOns: paymentSnapshot.newAddOnsPayload, // For email purposes
          ...fxTransactionFields
        };

        //Create transaction (add-ons already saved, so email will have correct balance)
        const { data: transactionData } =
          await createTransaction(transactionPayload);

        if (transactionData) {
          const { transactionid } = transactionData;

          const metadataUpdate = {
            metadata: {
              'Payment type': t('finalPayment'),
              'Booking number': String(bookingid),
              'Transaction ID': String(transactionid)
            }
          };
          if (hasFxSelection || managed) {
            // Human-readable metadata is optional after the verified transaction
            // is saved. A servicing failure must not offer to charge it again.
            if (paymentIntent.client_secret) {
              await updatePaymentIntent(paymentIntent.id, {
                ...metadataUpdate,
                clientSecret: paymentIntent.client_secret
              }).catch(() => {});
            }
          } else {
            await updatePaymentIntent(paymentIntent?.id, metadataUpdate);
          }

          // Track GA secondary payment submitted event
          const tripId = data?.trip?.tripid;
          const gaId = data?.tenant?.googleanalyticsid;

          if (gaId && tripId) {
            initGA(gaId);
            trackEvent('secondary_payment_submitted', {
              trip_id: tripId,
              booking_id: bookingid,
              amount: paymentSnapshot.operatorAmount
            });
          }

          navigate(
            `/bookings/${bookingid}/payment/success?tripid=${tripId}&bookingkey=${encodeURIComponent(bookingKey)}&transactionid=${transactionid}`,
            {
              state: {
                paymentMethod: type,
                transactionId: transactionid,
                transaction: transactionData,
                payerEmail: paymentSnapshot.customForm?.email?.trim() || '',
                amount: paymentSnapshot.operatorAmount,
                stripetransactionid: paymentIntent?.id,
                status,
                tripname: data?.trip?.tripname || ''
              }
            }
          );
        }
      } else {
        if (
          confirmedPayment.current &&
          ['requires_payment_method', 'canceled'].includes(status)
        ) {
          confirmedPayment.current = null;
          currencyState?.endPayment({ invalidated: status === 'canceled' });
          onPaymentConfirmed?.(false);
        }
        // Sometimes the payment was received and it cannot be requested again and Stripe returns error.
        // error?.payment_intent?.status !== 'succeeded'
        if (error && error?.payment_intent?.status !== 'succeeded') {
          if (error?.type !== 'validation_error') {
            ErrorToast(t('errors:bookingPaymentFailed'));
          }
        }
      }

      setIsProcessing(false);
    } catch (error) {
      ErrorToast(t('errors:bookingUnexpectedError'));
      setIsProcessing(false);
    } finally {
      submitting.current = false;
      if (!confirmedPayment.current) currencyState?.endPayment();
    }
  };

  const onChangeCustomInput = (event) => {
    const { name, value } = event.target;
    setCustomForm({ ...customForm, [name]: value });
    setCustomFormError({ ...customFormError, [name]: '' });
  };

  const handlePaymentMethodChange = useCallback(
    (event) => {
      if (event?.elementType === 'payment') {
        const currentPaymentMethod = event?.value;
        const { type } = currentPaymentMethod;
        if (
          confirmedPayment.current ||
          (type && !paymentMethodOrder?.includes(type))
        )
          return;
        const amount = selectedAmount || 0;
        const {
          totalWithFeesInChargeCurrency,
          applicationFeeNotIncluded,
          applicationFeeIncluded
        } = getFeesBreakdown(amount, fees ?? [], event?.value?.type ?? '');

        if (!hasFxSelection && !managed) {
          updatePaymentIntent(paymentIntentId, {
            amount: totalWithFeesInChargeCurrency,
            applicationFees: applicationFeeNotIncluded + applicationFeeIncluded
          });
        }

        if (type && type !== previousPaymentMethod?.type) {
          setPaymentMethod(type);
          setPreviousPaymentMethod(currentPaymentMethod);
        }
      }
    },
    [
      fees,
      paymentIntentId,
      previousPaymentMethod,
      paymentMethodOrder,
      selectedAmount,
      hasFxSelection,
      managed,
      setPaymentMethod
    ]
  );

  useEffect(() => {
    if (paymentOption !== 'partialManual') {
      setPartialAmountError('');
    }
    handlePaymentMethodChange();
  }, [paymentOption, handlePaymentMethodChange, setPartialAmountError]);

  useEffect(() => {
    if (paymentOption === 'partialManual') {
      handlePaymentMethodChange();
    }
  }, [partialAmount, paymentOption, handlePaymentMethodChange]);

  // Update payment intent amount when selectedAmount changes
  // This ensures Stripe payment intent always matches the user's selected payment option
  useEffect(() => {
    if (!hasFxSelection && !managed && paymentIntentId && selectedAmount > 0) {
      // Use selected payment method, or fall back to first available method, or default to 'card'
      const paymentType =
        previousPaymentMethod?.type || paymentMethodOrder?.[0] || 'card';

      try {
        const {
          totalWithFeesInChargeCurrency,
          applicationFeeNotIncluded,
          applicationFeeIncluded
        } = getFeesBreakdown(selectedAmount, fees ?? [], paymentType);

        updatePaymentIntent(paymentIntentId, {
          amount: totalWithFeesInChargeCurrency,
          applicationFees: applicationFeeNotIncluded + applicationFeeIncluded
        }).catch((error) => {
          console.error('Failed to update payment intent amount:', error);
          // Don't block user interaction, but log for monitoring
        });
      } catch (error) {
        console.error(
          'Error calculating fees for payment intent update:',
          error
        );
      }
    }
  }, [
    selectedAmount,
    paymentIntentId,
    fees,
    previousPaymentMethod,
    paymentMethodOrder,
    hasFxSelection,
    managed
  ]);

  return (
    <div id={id} {...rest} data-testid='payment-form'>
      <form
        id='payment-form'
        className='payment-form__container'
        onSubmit={handleSubmit}
      >
        <fieldset
          className='payment-form__fields'
          disabled={currencyState?.loading || completionLocked}
        >
          {/* Add-ons section at the top */}
          {addOnsSection && (
            <div style={{ marginBottom: '24px' }}>{addOnsSection}</div>
          )}

          {/* Display payment element only when booking is not paid in full or is paid in full but there are additional addons */}
          {displayPaymentSection && (
            <>
              {/* Payment options */}
              <div style={{ marginBottom: '20px' }}>
                <div className='payment-options__header payment-options__section-title'>
                  <span className='h2-semibold'>
                    {t('trip:completeYourPayment')}
                  </span>
                </div>
                <div className='payment-form__payment-option'>
                  {hasMultipleOptions
                    ? paymentChoices.map((choice) => (
                        <div
                          key={choice.id}
                          className='payment-form__payment-option-item'
                          style={{
                            borderColor:
                              paymentOption === choice.id ? primaryColor : '',

                            backgroundColor:
                              paymentOption === choice.id
                                ? `${primaryColor}0a`
                                : 'transparent'
                          }}
                          onClick={() =>
                            handlePaymentOptionSelection(choice.id, true)
                          }
                        >
                          <div className='payment-form__payment-option-item-header'>
                            <div className='payment-form__payment-option-item-header__radio'>
                              <XGRadio
                                id={`payment-option-${choice.id}`}
                                name='paymentOption'
                                size='small'
                                value={choice.id}
                                checked={paymentOption === choice.id}
                                onChange={(e) =>
                                  handlePaymentOptionSelection(
                                    e.target.value,
                                    true
                                  )
                                }
                                style={{
                                  color:
                                    paymentOption === choice?.id
                                      ? primaryColor
                                      : '#0009'
                                }}
                              />
                              <div className='payment-form__payment-option-item-header__label'>
                                <span className='payment-form__payment-option text-md-semibold'>
                                  {choice.label}
                                </span>
                              </div>
                            </div>

                            <div className='payment-form__payment-option-item-header__amount'>
                              {choice.type === 'input' ? (
                                <>
                                  <span style={{ marginRight: '8px' }}>
                                    {currencySymbol}
                                  </span>
                                  <XGInput
                                    id='payment-partial-amount'
                                    label=''
                                    size='small'
                                    value={partialAmount}
                                    disabled={paymentOption !== 'partialManual'}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setPartialAmount(value);

                                      if (paymentOption === 'partialManual') {
                                        const maxAllowedAmount =
                                          balancedue + allSelectedAddOnsTotal;
                                        if (value === '') {
                                          setPartialAmountError(
                                            t(
                                              'bookings:invalidPaymentAmountError'
                                            )
                                          );
                                        } else if (isNaN(parseFloat(value))) {
                                          setPartialAmountError(
                                            t(
                                              'bookings:invalidPaymentAmountError'
                                            )
                                          );
                                        } else if (parseFloat(value) < 1) {
                                          setPartialAmountError(
                                            `${t('bookings:minimumPaymentAmountError')} ${operatorMoney(1)}`
                                          );
                                        } else if (
                                          parseFloat(value) > maxAllowedAmount
                                        ) {
                                          setPartialAmountError(
                                            `${t('bookings:maximumPaymentAmountError')} ${operatorMoney(maxAllowedAmount)}`
                                          );
                                        } else {
                                          setPartialAmountError('');
                                        }
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!isNaN(parseFloat(partialAmount))) {
                                        setPartialAmount(
                                          parseFloat(partialAmount).toFixed(2)
                                        );
                                      }
                                    }}
                                    inputProps={{
                                      min: 1,
                                      max: balancedue + allSelectedAddOnsTotal,
                                      style: {
                                        width: '120px',
                                        textAlign: 'right'
                                      },
                                      inputMode: 'decimal'
                                    }}
                                  />
                                  <span style={{ marginLeft: '8px' }}>
                                    {currencyCode}
                                  </span>
                                </>
                              ) : (
                                <span
                                  className='payment-form__payment-option'
                                  style={{ fontWeight: 600, fontSize: '16px' }}
                                >
                                  {(choice.amountPrefix || '') +
                                    operatorMoney(choice.amount)}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className='w-100'>
                            {/* Show payment schedule breakdown for "Pay Next Installment" */}
                            {choice.showPaymentSchedule &&
                              choice.paymentSchedule &&
                              paymentOption === choice.id && (
                                <div className='payment-schedule-breakdown'>
                                  {choice.paymentSchedule.map((item, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        marginBottom:
                                          index <
                                          choice.paymentSchedule.length - 1
                                            ? '12px'
                                            : '0'
                                      }}
                                      className='payment-schedule-breakdown__item'
                                    >
                                      <div className='payment-schedule-breakdown__item-header'>
                                        <PaymentStatusIcon
                                          status={item.status}
                                        />
                                        <span
                                          style={{ color: 'var(--gray-500)' }}
                                        >
                                          {item.formattedAmount +
                                            ' - ' +
                                            item.description}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        </div>
                      ))
                    : paymentChoices.map((choice) => (
                        <div
                          key={choice.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                          className='payment-form__payment-option-item'
                        >
                          <span className='payment-form__payment-option'>
                            {choice.label}
                          </span>
                          <span
                            className='payment-form__payment-option'
                            style={{ fontWeight: 600 }}
                          >
                            {choice.type === 'input'
                              ? operatorMoney(selectedAmount)
                              : (choice.amountPrefix || '') +
                                operatorMoney(choice.amount)}
                          </span>
                        </div>
                      ))}
                  {paymentOption === 'partialManual' && partialAmountError && (
                    <div
                      style={{
                        color: 'red',
                        fontSize: '12px',
                        marginTop: '8px',
                        textAlign: 'left'
                      }}
                    >
                      {partialAmountError}
                    </div>
                  )}
                </div>
              </div>

              {!isAddToBalanceSelected && (
                <>
                  {/* Form fields remain the same */}
                  <XGInput
                    error={customFormError?.email}
                    hint={customFormError?.email}
                    label={`${t('common:formLabels.email')}*`}
                    name='email'
                    placeholder={t('common:formLabels.emailPlaceholder')}
                    id='add-day-title-input'
                    type='text'
                    value={customForm?.email || ''}
                    onChange={(evt) => {
                      onChangeCustomInput(evt);
                    }}
                    inputProps={{ maxLength: 250 }}
                  />
                  <div
                    style={{ display: 'flex', gap: '12px', margin: '20px 0' }}
                  >
                    <XGInput
                      error={customFormError?.firstName}
                      hint={customFormError?.firstName}
                      label={`${t('common:formLabels.firstName')}*`}
                      name='firstName'
                      placeholder={t('common:formLabels.firstNamePlaceholder')}
                      id='add-day-title-input'
                      type='text'
                      value={customForm?.firstName || ''}
                      onChange={(evt) => {
                        onChangeCustomInput(evt);
                      }}
                      inputProps={{ maxLength: 250 }}
                      style={{ width: '100%' }}
                    />
                    <XGInput
                      error={customFormError?.lastName}
                      hint={customFormError?.lastName}
                      label={`${t('common:formLabels.lastName')}*`}
                      name='lastName'
                      placeholder={t('common:formLabels.lastNamePlaceholder')}
                      id='add-day-title-input'
                      type='text'
                      value={customForm?.lastName || ''}
                      onChange={(evt) => {
                        onChangeCustomInput(evt);
                      }}
                      inputProps={{ maxLength: 250 }}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <PaymentElement
                    options={{ ...paymentElementOptions, paymentMethodOrder }}
                    onChange={handlePaymentMethodChange}
                  />
                  {requiresFullBillingAddress() && (
                    <div style={{ marginBottom: '20px' }}>
                      <AddressElement
                        options={{
                          mode: 'billing',
                          fields: {
                            name: 'never'
                          },
                          defaultValues: {
                            name: `${customForm?.firstName || ''} ${customForm?.lastName || ''}`.trim()
                          }
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </fieldset>
        <input type='submit' style={{ display: 'none' }} ref={submitRef} />
      </form>
    </div>
  );
};

PaymentForm.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.object,
  paymentIntentId: PropTypes.string,
  currencyState: PropTypes.object,
  currencyConfig: PropTypes.object,
  onPaymentConfirmed: PropTypes.func,
  selectedCurrency: PropTypes.shape({
    currency: PropTypes.string,
    chargeAmount: PropTypes.number,
    fxQuoteId: PropTypes.string,
    lockedRate: PropTypes.number,
    expiresAt: PropTypes.number,
    operatorBookingCredit: PropTypes.number
  }),
  setPaymentMethod: PropTypes.func,
  paymentMethodOrder: PropTypes.array,
  paymentOption: PropTypes.string,
  setPaymentOption: PropTypes.func,
  partialAmount: PropTypes.string,
  setPartialAmount: PropTypes.func,
  nextInstallment: PropTypes.object,
  pastDueAmount: PropTypes.number,
  allSelectedAddOnsTotal: PropTypes.number,
  dueNowSelectedAddOnsTotal: PropTypes.number,
  deferredAddOnsTotal: PropTypes.number,
  showAddToBalanceOption: PropTypes.bool,
  settings: PropTypes.object,
  submitRef: PropTypes.object,
  customForm: PropTypes.object,
  setCustomForm: PropTypes.func,
  customFormError: PropTypes.object,
  setCustomFormError: PropTypes.func,
  partialAmountError: PropTypes.string,
  setPartialAmountError: PropTypes.func,
  setIsProcessing: PropTypes.func,
  onManualPaymentSelection: PropTypes.func,
  paymentSchedule: PropTypes.array,
  newAddOnsPayload: PropTypes.array,
  addOnsSection: PropTypes.node
};

export default PaymentForm;
